import { useCallback, useEffect, useRef, useState } from "react";
import { translationsApi } from "./api";
import type { TranslationLocalesResponse } from "./types";

// Une génération dure de quelques secondes à plusieurs minutes : on sonde tant
// qu'une locale est en cours, et on s'arrête dès que plus rien ne tourne.
const POLL_INTERVAL_MS = 5000;

export function useTranslationLocales() {
	const [data, setData] = useState<TranslationLocalesResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const isMounted = useRef(true);

	const load = useCallback(async () => {
		try {
			const response = await translationsApi.listLocales();
			if (!isMounted.current) return;
			setData(response);
			setError("");
		} catch (err) {
			if (!isMounted.current) return;
			setError(err instanceof Error ? err.message : "load_failed");
		} finally {
			if (isMounted.current) setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		isMounted.current = true;
		void load();
		return () => {
			isMounted.current = false;
		};
	}, [load]);

	const isTranslating = data?.locales.some((locale) => locale.status === "translating") ?? false;

	useEffect(() => {
		if (!isTranslating) return;
		const id = setInterval(() => void load(), POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [isTranslating, load]);

	return { data, isLoading, error, isTranslating, reload: load };
}
